// downloadProductsCSV.js

import { saveAs } from "file-saver"

const PRODUCTS_PER_PAGE = 250 // Shopify's max limit per page
const DEFAULT_FREE_EXPORT_LIMIT = 50

// Helper function to escape CSV values (wrap in quotes if necessary)
function escapeCsvValue(value) {
  if (value === undefined || value === null) {
    return "" // If it's null or undefined, return empty string
  }

  // Convert value to string if it's not already a string
  value = String(value)

  // If value contains a comma, double quote, or newline, wrap in quotes and escape inner quotes
  if (value.indexOf(",") > -1 || value.indexOf('"') > -1 || value.indexOf("\n") > -1) {
    return `"${value.replace(/"/g, '""')}"` // Escape quotes by replacing " with ""
  }

  return value
}

// Function to clean HTML content in descriptions (though not directly used in the provided Shopify structure)
function cleanDescription(description) {
  try {
    const doc = new DOMParser().parseFromString(description, "text/html")
    return doc.body.textContent || "" // Extract plain text from HTML
  } catch (e) {
    console.warn("Error parsing HTML description:", e)
    return description // Return original if parsing fails
  }
}

async function getProductData(url, currentPage) {
  try {
    const apiUrl = `${url}/products.json?page=${currentPage}&limit=${PRODUCTS_PER_PAGE}`
    console.log("downloadProductsCSV: Fetching URL:", apiUrl)
    const response = await fetch(apiUrl)
    if (!response.ok) {
      console.error(`Failed to fetch products on page ${currentPage}:`, response.status)
      return {
        products: [],
        hasNext: false,
      }
    }
    const data = await response.json()
    const fetchedProducts = Array.isArray(data.products) ? data.products : []
    const hasNext = fetchedProducts.length === PRODUCTS_PER_PAGE
    console.log(
      "downloadProductsCSV: Received",
      fetchedProducts.length,
      "products on page",
      currentPage,
      "hasNext:",
      hasNext,
    )
    return {
      products: fetchedProducts,
      hasNext,
    }
  } catch (error) {
    console.error("Error fetching product data:", error)
    return {
      products: [],
      hasNext: false,
    }
  }
}

const downloadProductsCSV = async (websiteUrl, platform, exportDataType, limit) => {
  const products = []
  let page = 1
  let hasMore = true
  const effectiveProductLimit = limit !== undefined ? Number.parseInt(limit, 10) : Number.POSITIVE_INFINITY
  let fetchedCount = 0

  while (hasMore && fetchedCount < effectiveProductLimit) {
    const { products: newProducts, hasNext } = await getProductData(websiteUrl, page)

    if (newProducts && newProducts.length > 0) {
      const productsToAdd = newProducts.slice(0, effectiveProductLimit - fetchedCount)
      products.push(...productsToAdd)
      fetchedCount += productsToAdd.length
      page++
      hasMore = hasNext && fetchedCount < effectiveProductLimit && newProducts.length === PRODUCTS_PER_PAGE
    } else {
      hasMore = false // Stop if no new products are returned
    }
    console.log(
      "downloadProductsCSV: Current total products:",
      products.length,
      "Fetched Count:",
      fetchedCount,
      "Page:",
      page,
      "hasMore:",
      hasMore,
    )
  }

  if (products.length === 0) {
    alert("No products found to export.")
    return
  }

  let csvData
  let filename

  if (platform === "Shopify") {
    const shopifyHeaders = [
      "Handle",
      "Title",
      "Body (HTML)",
      "Vendor",
      "Type",
      "Tags",
      "Published",
      "Option1 Name",
      "Option1 Value",
      "Option2 Name",
      "Option2 Value",
      "Option3 Name",
      "Option3 Value",
      "Variant SKU",
      "Variant Grams",
      "Variant Inventory Tracker",
      "Variant Inventory Qty",
      "Variant Inventory Policy",
      "Variant Fulfillment Service",
      "Variant Price",
      "Variant Compare At Price",
      "Variant Requires Shipping",
      "Variant Taxable",
      "Variant Barcode",
      "Image Src",
      "Image Position",
      "Image Alt Text",
      "Gift Card",
    ]

    const allRows = []

    products.forEach((product) => {
      // First, add all variants
      product.variants.forEach((variant) => {
        const variantRow = {
          Handle: product.handle || "",
          Title: product.title || "",
          "Body (HTML)": product.body_html || "",
          Vendor: product.vendor || "",
          Type: product.product_type || "",
          Tags: product.tags ? product.tags.join(",") : "",
          Published: "TRUE",
          "Option1 Name": product.options && product.options[0] ? product.options[0].name : "",
          "Option1 Value": variant.option1 || "",
          "Option2 Name": product.options && product.options[1] ? product.options[1].name : "",
          "Option2 Value": variant.option2 || "",
          "Option3 Name": product.options && product.options[2] ? product.options[2].name : "",
          "Option3 Value": variant.option3 || "",
          "Variant SKU": variant.sku || "",
          "Variant Grams": String(variant.grams || ""),
          "Variant Inventory Tracker": "shopify",
          "Variant Inventory Qty": "1",
          "Variant Inventory Policy": "deny",
          "Variant Fulfillment Service": "manual",
          "Variant Price": String(variant.price || ""),
          "Variant Compare At Price": String(variant.compare_at_price || ""),
          "Variant Requires Shipping": variant.requires_shipping ? "TRUE" : "FALSE",
          "Variant Taxable": variant.taxable ? "TRUE" : "FALSE",
          "Variant Barcode": "",
          "Image Src": "",
          "Image Position": "",
          "Image Alt Text": "",
          "Gift Card": "FALSE",
        }
        allRows.push(variantRow)
      })

      // Then, add all images
      if (product.images && product.images.length > 0) {
        product.images.forEach((image, imageIndex) => {
          const imageRow = {
            Handle: product.handle || "",
            Title: "",
            "Body (HTML)": "",
            Vendor: "",
            Type: "",
            Tags: "",
            Published: "",
            "Option1 Name": "",
            "Option1 Value": "",
            "Option2 Name": "",
            "Option2 Value": "",
            "Option3 Name": "",
            "Option3 Value": "",
            "Variant SKU": "",
            "Variant Grams": "",
            "Variant Inventory Tracker": "",
            "Variant Inventory Qty": "",
            "Variant Inventory Policy": "",
            "Variant Fulfillment Service": "",
            "Variant Price": "",
            "Variant Compare At Price": "",
            "Variant Requires Shipping": "",
            "Variant Taxable": "",
            "Variant Barcode": "",
            "Image Src": image.src || "",
            "Image Position": String(imageIndex + 1),
            "Image Alt Text": "",
            "Gift Card": "",
          }
          allRows.push(imageRow)
        })
      }
    })

    let csv = shopifyHeaders.join(",") + "\n"
    allRows.forEach((row) => {
      csv += shopifyHeaders.map((header) => escapeCsvValue(row[header] || "")).join(",") + "\n"
    })

    csvData = csv
    filename = limit ? `free_export_products_shopify.csv` : `products_shopify.csv`
  } else if (platform === "WooCommerce") {
    const wooHeaders = [
      "ID",
      "Name",
      "Type",
      "SKU",
      "Short description",
      "Description",
      "In stock?",
      "Stock",
      "Sale price",
      "Regular price",
      "Categories",
      "Tags",
      "Vendor",
      "Images",
      "Parent ID",
      "Attribute 1 name",
      "Attribute 1 value(s)",
      "Attribute 2 name",
      "Attribute 2 value(s)",
      "Attribute 3 name",
      "Attribute 3 value(s)",
    ]

    const csvRows = []
    csvRows.push(wooHeaders.join(","))

    const groupedProducts = {}
    products.forEach((product) => {
      if (!groupedProducts[product.id]) {
        groupedProducts[product.id] = []
      }
      groupedProducts[product.id].push(product)
    })

    Object.values(groupedProducts).forEach((productGroup) => {
      const firstProduct = productGroup[0]
      const cleanedDescription = cleanDescription(firstProduct.body_html || firstProduct.handle)
      const hasVariants = firstProduct.variants && firstProduct.variants.length > 1
      const allImages = firstProduct.images ? firstProduct.images.map((image) => image.src).join(",") : ""
      const productTags = Array.isArray(firstProduct.tags) ? firstProduct.tags.join(",") : ""

      if (!hasVariants) {
        const wooProduct = {
          ID: firstProduct.id,
          Name: firstProduct.title,
          Type: "simple",
          SKU:
            firstProduct.variants && firstProduct.variants[0] && firstProduct.variants[0].sku
              ? firstProduct.variants[0].sku
              : firstProduct.handle || "",
          "Short description": firstProduct.handle || "",
          Description: cleanedDescription,
          "In stock?": firstProduct.variants && firstProduct.variants[0] && firstProduct.variants[0].available ? 1 : 0,
          Stock: "",
          "Sale price":
            firstProduct.variants && firstProduct.variants[0] && firstProduct.variants[0].compare_at_price
              ? firstProduct.variants[0].compare_at_price
              : "",
          "Regular price":
            firstProduct.variants && firstProduct.variants[0] && firstProduct.variants[0].price
              ? firstProduct.variants[0].price
              : "",
          Categories: firstProduct.product_type || "",
          Tags: productTags,
          Vendor: firstProduct.vendor || "",
          Images: allImages,
          "Parent ID": "",
          "Attribute 1 name":
            firstProduct.options && firstProduct.options[0] && firstProduct.options[0].name
              ? firstProduct.options[0].name
              : "",
          "Attribute 1 value(s)":
            firstProduct.variants && firstProduct.variants[0] ? firstProduct.variants[0].option1 : "",
          "Attribute 2 name":
            firstProduct.options && firstProduct.options[1] && firstProduct.options[1].name
              ? firstProduct.options[1].name
              : "",
          "Attribute 1 value(s)":
            firstProduct.variants && firstProduct.variants[0] ? firstProduct.variants[0].option2 : "",
          "Attribute 3 name":
            firstProduct.options && firstProduct.options[2] && firstProduct.options[2].name
              ? firstProduct.options[2].name
              : "",
          "Attribute 1 value(s)":
            firstProduct.variants && firstProduct.variants[0] ? firstProduct.variants[0].option3 : "",
        }
        csvRows.push(wooHeaders.map((header) => escapeCsvValue(wooProduct[header])).join(","))
      } else {
        const wooParentProduct = {
          ID: firstProduct.id,
          Name: firstProduct.title,
          Type: "variable",
          SKU: firstProduct.handle || "",
          "Short description": firstProduct.handle || "",
          Description: cleanedDescription,
          "In stock?": 1,
          Stock: "",
          "Sale price": "",
          "Regular price": "",
          Categories: firstProduct.product_type || "",
          Tags: productTags,
          Vendor: firstProduct.vendor || "",
          Images: allImages,
          "Parent ID": "",
          "Attribute 1 name":
            firstProduct.options && firstProduct.options[0] && firstProduct.options[0].name
              ? firstProduct.options[0].name
              : "",
          "Attribute 1 value(s)":
            firstProduct.options && firstProduct.options[0] && firstProduct.options[0].values
              ? firstProduct.options[0].values.join("|")
              : "",
          "Attribute 2 name":
            firstProduct.options && firstProduct.options[1] && firstProduct.options[1].name
              ? firstProduct.options[1].name
              : "",
          "Attribute 2 value(s)":
            firstProduct.options && firstProduct.options[1] && firstProduct.options[1].values
              ? firstProduct.options[1].values.join("|")
              : "",
          "Attribute 3 name":
            firstProduct.options && firstProduct.options[2] && firstProduct.options[2].name
              ? firstProduct.options[2].name
              : "",
          "Attribute 3 value(s)":
            firstProduct.options && firstProduct.options[2] && firstProduct.options[2].values
              ? firstProduct.options[2].values.join("|")
              : "",
        }
        csvRows.push(wooHeaders.map((header) => escapeCsvValue(wooParentProduct[header])).join(","))

        firstProduct.variants.forEach((variant) => {
          const variantImage = variant.featured_image ? variant.featured_image.src : ""
          const wooVariantProduct = {
            ID: variant.id,
            Name: `${firstProduct.title} - ${variant.title}` || firstProduct.title,
            Type: "variation",
            SKU: variant.sku || "",
            "Short description": firstProduct.handle || "",
            Description: "",
            "In stock?": variant.available ? 1 : 0,
            Stock: "",
            "Sale price": variant.compare_at_price || "",
            "Regular price": variant.price || "",
            Categories: "",
            Tags: "",
            Vendor: firstProduct.vendor || "",
            Images: variantImage,
            "Parent ID": `id:${firstProduct.id}`,
            "Attribute 1 name":
              firstProduct.options && firstProduct.options[0] && firstProduct.options[0].name
                ? firstProduct.options[0].name
                : "",
            "Attribute 1 value(s)": variant.option1 || "",
            "Attribute 2 name":
              firstProduct.options && firstProduct.options[1] && firstProduct.options[1].name
                ? firstProduct.options[1].name
                : "",
            "Attribute 1 value(s)": variant.option2 || "",
            "Attribute 3 name":
              firstProduct.options && firstProduct.options[2] && firstProduct.options[2].name
                ? firstProduct.options[2].name
                : "",
            "Attribute 1 value(s)": variant.option3 || "",
          }
          csvRows.push(wooHeaders.map((header) => escapeCsvValue(wooVariantProduct[header])).join(","))
        })
      }
    })

    csvData = csvRows.join("\n")
    filename = limit ? `free_export_products_woocommerce.csv` : `products_woocommerce.csv`
  } else {
    alert(`Unsupported platform: ${platform}`)
    return
  }

  if (csvData) {
    const blob = new Blob([csvData], {
      type: "text/csv;charset=utf-8",
    })
    saveAs(blob, filename)
  }
}

export default downloadProductsCSV
